import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Dog, Mail, Phone, Calendar, RefreshCw, ArrowLeft, User, PawPrint } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusColors = {
  new: 'bg-blue-100 text-blue-700 border-blue-200',
  contacted: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  converted: 'bg-green-100 text-green-700 border-green-200',
  closed: 'bg-stone-100 text-stone-600 border-stone-200'
};

const serviceLabels = {
  overnight: 'Overnight Boarding',
  daycare: 'Doggy Daycare',
  extended: 'Extended Stay'
};

const sizeLabels = {
  small: 'Small (under 25 lbs)',
  medium: 'Medium (25-50 lbs)',
  large: 'Large (50-100 lbs)',
  xlarge: 'X-Large (100+ lbs)'
};

const AdminDashboard = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/leads`);
      setLeads(response.data);
    } catch (error) {
      console.error('Error fetching leads:', error);
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads();
  }, []);

  const updateLeadStatus = async (leadId, newStatus) => {
    setUpdatingId(leadId);
    try {
      await axios.patch(`${API}/leads/${leadId}`, { status: newStatus });
      setLeads(leads.map(lead => 
        lead.id === leadId ? { ...lead, status: newStatus } : lead
      ));
      toast.success('Status updated');
    } catch (error) {
      console.error('Error updating lead:', error);
      toast.error('Failed to update status');
    } finally {
      setUpdatingId(null);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const newLeadsCount = leads.filter(l => l.status === 'new').length;

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <header className="bg-white border-b border-stone-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <a href="/" className="flex items-center gap-2 text-stone-600 hover:text-amber-600 transition-colors">
                <ArrowLeft className="w-5 h-5" />
                <span className="text-sm font-medium">Back to Site</span>
              </a>
              <div className="h-6 w-px bg-stone-200" />
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-amber-500 rounded-lg flex items-center justify-center">
                  <Dog className="w-5 h-5 text-white" />
                </div>
                <h1 className="font-bold text-stone-800">Lead Dashboard</h1>
              </div>
            </div>
            <Button
              onClick={fetchLeads}
              variant="outline"
              size="sm"
              className="gap-2"
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl p-4 border border-stone-200">
            <p className="text-sm text-stone-500">Total Leads</p>
            <p className="text-2xl font-bold text-stone-800">{leads.length}</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-blue-200 bg-blue-50">
            <p className="text-sm text-blue-600">New</p>
            <p className="text-2xl font-bold text-blue-700">{newLeadsCount}</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-yellow-200 bg-yellow-50">
            <p className="text-sm text-yellow-600">Contacted</p>
            <p className="text-2xl font-bold text-yellow-700">
              {leads.filter(l => l.status === 'contacted').length}
            </p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-green-200 bg-green-50">
            <p className="text-sm text-green-600">Converted</p>
            <p className="text-2xl font-bold text-green-700">
              {leads.filter(l => l.status === 'converted').length}
            </p>
          </div>
        </div>

        {/* Leads Table */}
        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-stone-200">
            <h2 className="font-semibold text-stone-800">All Leads</h2>
          </div>
          
          {loading ? (
            <div className="p-12 text-center">
              <RefreshCw className="w-8 h-8 text-stone-400 animate-spin mx-auto mb-4" />
              <p className="text-stone-500">Loading leads...</p>
            </div>
          ) : leads.length === 0 ? (
            <div className="p-12 text-center">
              <PawPrint className="w-12 h-12 text-stone-300 mx-auto mb-4" />
              <p className="text-stone-500">No leads yet</p>
              <p className="text-sm text-stone-400">Leads will appear here when someone fills out the quote form</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Contact</TableHead>
                  <TableHead>Dog Info</TableHead>
                  <TableHead>Service</TableHead>
                  <TableHead>Message</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leads.map((lead) => (
                  <TableRow key={lead.id}>
                    <TableCell>
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-stone-400" />
                          <span className="font-medium text-stone-800">{lead.name}</span>
                        </div>
                        <a href={`mailto:${lead.email}`} className="flex items-center gap-2 text-sm text-stone-500 hover:text-amber-600">
                          <Mail className="w-3 h-3" />
                          {lead.email}
                        </a>
                        <a href={`tel:${lead.phone}`} className="flex items-center gap-2 text-sm text-stone-500 hover:text-amber-600">
                          <Phone className="w-3 h-3" />
                          {lead.phone}
                        </a>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <PawPrint className="w-4 h-4 text-amber-500" />
                          <span className="font-medium">{lead.dog_name}</span>
                        </div>
                        <p className="text-sm text-stone-500">{sizeLabels[lead.dog_size] || lead.dog_size}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                        {serviceLabels[lead.service_type] || lead.service_type}
                      </Badge>
                    </TableCell>
                    <TableCell className="max-w-xs">
                      <p className="text-sm text-stone-600 truncate">
                        {lead.message || '-'}
                      </p>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 text-sm text-stone-500">
                        <Calendar className="w-3 h-3" />
                        {formatDate(lead.created_at)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Select
                        value={lead.status}
                        onValueChange={(value) => updateLeadStatus(lead.id, value)}
                        disabled={updatingId === lead.id}
                      >
                        <SelectTrigger className={`w-32 h-8 text-xs ${statusColors[lead.status]}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">New</SelectItem>
                          <SelectItem value="contacted">Contacted</SelectItem>
                          <SelectItem value="converted">Converted</SelectItem>
                          <SelectItem value="closed">Closed</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
